// The side menu of an HTML plan's page (today's plans/PlanNav.tsx), in the outline's place: its sections,
// and under Phases its phases with their state's icon and how many of
// their issues landed. It marks the part being read, and a phase it points to
// opens. From 900 px it stays beside the plan as the reading scrolls;
// narrower, it stands above the plan.
import { useEffect, useMemo, useRef, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react'
import { StateIcon } from '../components/Icons'
import { useNavigate } from 'react-router-dom'
import { cx } from '../transcript/bits'
import { useActiveHeading } from './useActiveHeading'
import { useTabScope } from '../workspace/context'
import { usePlanFacts, type PlanViewProps } from './PlanView'
import { revealTarget } from './reveal'
import { PHASE_ICON, type PlanFacts } from './state'
import { planWords as w } from './words'
import './planview.css'

export type PlanNavProps = Pick<PlanViewProps, 'model' | 'listed' | 'issues'>

interface Entry {
  id: string
  label: ReactNode
  kind: 'section' | 'phases' | 'phase'
  count?: ReactNode
}

/** The menu's entries, in the page's order. */
export function navEntries(facts: PlanFacts, model: PlanNavProps['model']): Entry[] {
  const entries: Entry[] = [
    { id: 'pv-overview', label: w.overview, kind: 'section' },
    { id: 'pv-progress', label: w.progress, kind: 'section' },
  ]
  entries.push({ id: 'pv-phases', label: model.phasesTitle, kind: 'phases' })
  for (const phase of facts.phases) {
    entries.push({
      id: phase.phase.id,
      kind: 'phase',
      label: (
        <>
          <StateIcon state={PHASE_ICON[phase.state]} size={12} />
          <span className="pv-nav-name">{phase.phase.title}</span>
        </>
      ),
      count: phase.total > 0 ? <span className="pv-nav-count">{`${phase.landed}/${phase.total}`}</span> : undefined,
    })
  }
  if (facts.elsewhere.length > 0) entries.push({ id: 'pv-elsewhere', label: w.elsewhere, kind: 'section' })
  if (model.decisions.length > 0) entries.push({ id: 'pv-decisions', label: w.decisions, kind: 'section' })
  if (model.annex) entries.push({ id: 'pv-annex', label: w.annex, kind: 'section' })
  return entries
}

export function PlanNav(props: PlanNavProps) {
  const scope = useTabScope()
  const facts = usePlanFacts(props)
  const entries = useMemo(() => navEntries(facts, props.model), [facts, props.model])
  const ids = useMemo(() => entries.map((entry) => entry.id), [entries])
  const active = useActiveHeading(ids) ?? ids[0] ?? null
  const navigate = useNavigate()
  const box = useRef<HTMLElement>(null)

  // The marked entry stays in sight inside the menu's own scroll.
  useEffect(() => {
    const container = box.current
    const link = container?.querySelector<HTMLElement>('a[aria-current]')
    if (!container || !link || container.scrollHeight <= container.clientHeight) return
    const outer = container.getBoundingClientRect()
    const inner = link.getBoundingClientRect()
    if (inner.top < outer.top + 24) container.scrollTop -= outer.top + 24 - inner.top
    else if (inner.bottom > outer.bottom - 24) container.scrollTop += inner.bottom - (outer.bottom - 24)
  }, [active])

  // A body off the structure is shown as written, and has no parts to go to.
  if (props.model.diagnostics.length > 0) return null

  const follow = (event: ReactMouseEvent<HTMLAnchorElement>, id: string) => {
    if (event.defaultPrevented || event.button !== 0) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    revealTarget(id, true, scope?.surface())
    navigate(`#${id}`, { replace: true })
  }

  const link = (entry: Entry) => (
    <a
      key={entry.id}
      href={`#${entry.id}`}
      className={cx(entry.kind === 'phase' && 'pv-nav-phase')}
      aria-current={entry.id === active ? 'location' : undefined}
      onClick={(event) => follow(event, entry.id)}
    >
      {entry.label}
      {entry.count}
    </a>
  )

  return (
    <nav className="pv-nav" aria-label={w.nav} ref={box}>
      <p className="pv-nav-title">{w.nav}</p>
      {entries.map((entry) =>
        entry.kind === 'phases' ? (
          <p key={entry.id} className="pv-nav-phases">
            {link(entry)}
          </p>
        ) : (
          link(entry)
        ),
      )}
    </nav>
  )
}
